/**
 * Execute skill insertion plans in the open Word document.
 */
import { insertAtMultipleAgentAnchors } from '../../../word/api/AgentDocumentInserter';
import { DocumentReader } from '../../../word/api/DocumentReader';
import { TrackChangesManager } from '../../../word/trackChanges/TrackChangesManager';
import { log, logError } from '../../../utils/logging';
import type { SkillInsertionPlan } from './types';

export async function executeSkillInsertions(
  insertions: SkillInsertionPlan[],
): Promise<{ succeeded: number; failed: number; details: string[] }> {
  const needsTrackChanges = insertions.some((ins) => ins.format === 'track_changes');
  if (needsTrackChanges) {
    const enabled = await TrackChangesManager.ensureEnabled();
    if (!enabled) {
      logError('[SkillWordExecutor] track changes could not be enabled');
      return {
        succeeded: 0,
        failed: insertions.length,
        details: insertions.map(
          (_, i) => `Insertion ${i + 1}: track changes unavailable in this Word host`,
        ),
      };
    }
    log('[SkillWordExecutor] track changes enabled for skill insertions');
  }

  const batchItems = insertions.map((ins) => ({
    text: ins.client_request_text,
    afterAgentParagraphIndex: ins.target_paragraph_index,
    trackChangesAuthor:
      ins.format === 'track_changes' ? (ins.author ?? 'Unknown').trim() || 'Unknown' : undefined,
  }));

  const result = await insertAtMultipleAgentAnchors(batchItems);
  log('[SkillWordExecutor] complete', {
    succeeded: result.succeeded,
    failed: result.failed,
  });
  return {
    succeeded: result.succeeded,
    failed: result.failed,
    details: result.details,
  };
}

export async function getDocumentParagraphsForSkill(): Promise<
  Array<{ index: number; text: string }>
> {
  const { paragraphs } = await DocumentReader.getFullDocumentContext();
  // AgentDocumentInserter maps target_paragraph_index to the Nth non-empty body
  // paragraph (0..n-1). DocumentReader keeps Office body.paragraphs indices which
  // include gaps for empty paragraphs — re-number so engine plans match Word insert.
  return paragraphs.map((p, sequentialIndex) => ({
    index: sequentialIndex,
    text: p.text,
  }));
}
